import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE, api } from "@/lib/apiClient";
import { readPdfFileAsDataUrl } from "@/lib/pdfUpload";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { resolveBrandingAssetUrl } from "@shared/brandingAssetUrl";
import {
  EMPTY_LETTERHEAD,
  letterheadFromApi,
  type InvoiceLetterhead,
  type InvoiceLetterheadForm,
} from "@/pages/stammdaten/invoiceLetterheadTypes";

interface LetterheadSettingsResponse {
  invoice_letterhead: InvoiceLetterhead;
}

interface InvoiceLetterheadSectionProps {
  canWrite: boolean;
  /** Wird nach erfolgreichem Speichern aufgerufen (z. B. Branding neu laden). */
  onSaved?: () => void;
}

type BrandImageFieldName = "logo_url" | "favicon_url";

const BRAND_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/svg+xml,.svg,image/x-icon,.ico";

function BrandImageField({
  label,
  hint,
  value,
  canWrite,
  uploading,
  onFile,
  onChange,
  onClear,
}: {
  label: string;
  hint: string;
  value: string;
  canWrite: boolean;
  uploading: boolean;
  onFile: (file: File) => void;
  onChange: (value: string) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const preview = resolveBrandingAssetUrl(value, API_BASE);
  const hasImage = Boolean(value.trim());

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-separator bg-grouped p-4 sm:col-span-2">
      <span className="text-sm font-medium">{label}</span>
      {preview ? (
        <img
          src={preview}
          alt=""
          className="max-h-16 max-w-[16rem] object-contain"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      ) : (
        <span className="text-sm text-label-tertiary">Kein Bild hinterlegt</span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={BRAND_IMAGE_ACCEPT}
        className="hidden"
        disabled={!canWrite || uploading}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onFile(file);
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        {canWrite ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={uploading}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {hasImage ? "Bild ersetzen" : "Bild hochladen"}
          </Button>
        ) : null}
        {canWrite && hasImage ? (
          <Button type="button" variant="ghost" size="sm" onClick={onClear}>
            Entfernen
          </Button>
        ) : null}
      </div>
      <label className="flex flex-col gap-1 text-sm">
        oder Bild-URL
        <input
          type="text"
          className="eh-input"
          placeholder="https://…/bild.png"
          value={value}
          disabled={!canWrite}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      <span className="text-xs text-label-tertiary">{hint}</span>
    </div>
  );
}

export function InvoiceLetterheadSection({ canWrite, onSaved }: InvoiceLetterheadSectionProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [letterhead, setLetterhead] = useState<InvoiceLetterheadForm>(EMPTY_LETTERHEAD);
  const [letterheadPdfUploading, setLetterheadPdfUploading] = useState(false);
  const [brandImageUploading, setBrandImageUploading] = useState<BrandImageFieldName | null>(null);
  const [letterheadPreviewLoading, setLetterheadPreviewLoading] = useState(false);
  const letterheadPdfInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<LetterheadSettingsResponse>("/api/v1/settings")
      .then((settings) => {
        setLetterhead(letterheadFromApi(settings.invoice_letterhead));
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite || brandImageUploading) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch<LetterheadSettingsResponse>("/api/v1/settings/invoice-letterhead", {
        company_name: letterhead.company_name.trim() || null,
        street: letterhead.street.trim() || null,
        postal_code: letterhead.postal_code.trim() || null,
        city: letterhead.city.trim() || null,
        country: letterhead.country.trim() || null,
        tax_id: letterhead.tax_id.trim() || null,
        email: letterhead.email.trim() || null,
        iban: letterhead.iban.trim() || null,
        bic: letterhead.bic.trim() || null,
        logo_url: letterhead.logo_url.trim() || null,
        favicon_url: letterhead.favicon_url.trim() || null,
        footer_text: letterhead.footer_text.trim() || null,
        letterhead_pdf_data: letterhead.letterhead_pdf_data.trim() || null,
      });
      setSuccess(true);
      onSaved?.();
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Grundeinstellungen konnten nicht gespeichert werden");
    } finally {
      setSaving(false);
    }
  }

  async function openLetterheadPreview() {
    setLetterheadPreviewLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/v1/settings/invoice-letterhead/preview-pdf", {
        credentials: "include",
      });
      if (!res.ok) {
        let message = `${res.status} ${res.statusText}`;
        const ct = res.headers.get("content-type") || "";
        if (ct.includes("application/json")) {
          try {
            const parsed = (await res.json()) as { detail?: unknown };
            if (typeof parsed.detail === "string") message = parsed.detail;
          } catch {
            /* ignore */
          }
        }
        throw new Error(message);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const tab = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (!tab) {
        URL.revokeObjectURL(blobUrl);
        throw new Error("Pop-up blockiert. Bitte Pop-ups erlauben.");
      }
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Vorschau konnte nicht geladen werden.");
    } finally {
      setLetterheadPreviewLoading(false);
    }
  }

  async function onLetterheadPdfSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !canWrite) return;
    setLetterheadPdfUploading(true);
    setError(null);
    try {
      const dataUrl = await readPdfFileAsDataUrl(file);
      setLetterhead((prev) => ({ ...prev, letterhead_pdf_data: dataUrl }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Briefpapier konnte nicht geladen werden.");
    } finally {
      setLetterheadPdfUploading(false);
    }
  }

  function clearLetterheadPdf() {
    setLetterhead((prev) => ({ ...prev, letterhead_pdf_data: "" }));
  }

  async function onBrandImageSelected(field: BrandImageFieldName, file: File) {
    if (!canWrite) return;
    setBrandImageUploading(field);
    setError(null);
    setSuccess(false);
    try {
      const body = new FormData();
      body.append("file", file);
      const data = await api.post<{ url: string }>("/api/v1/settings/branding/media", undefined, {
        rawBody: body,
      });
      setLetterhead((prev) => ({ ...prev, [field]: data.url }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bild konnte nicht hochgeladen werden.");
    } finally {
      setBrandImageUploading(null);
    }
  }

  const hasLetterheadPdf = Boolean(letterhead.letterhead_pdf_data.trim());

  return (
    <Card
      id="betrieb"
      className="scroll-mt-24"
      title="Campingplatz, Logo & Briefkopf"
      description="Name, Adresse, Bankverbindung, Logo und Favicon erscheinen in der Anwendung, auf Rechnungs-PDFs, in E-Mails sowie auf Online-Buchung, Gäste-Tablet und Infobildschirm."
      bodyClassName="space-y-4"
    >
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <form onSubmit={(e) => void onSave(e)} className="space-y-3">
          <fieldset className="grid gap-3 sm:grid-cols-2" disabled={!canWrite}>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Name des Campingplatzes
              <input
                className="eh-input"
                value={letterhead.company_name}
                onChange={(e) =>
                  setLetterhead((prev) => ({ ...prev, company_name: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Straße
              <input
                className="eh-input"
                value={letterhead.street}
                onChange={(e) => setLetterhead((prev) => ({ ...prev, street: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              PLZ
              <input
                className="eh-input"
                value={letterhead.postal_code}
                onChange={(e) =>
                  setLetterhead((prev) => ({ ...prev, postal_code: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Ort
              <input
                className="eh-input"
                value={letterhead.city}
                onChange={(e) => setLetterhead((prev) => ({ ...prev, city: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Land
              <input
                className="eh-input"
                value={letterhead.country}
                onChange={(e) =>
                  setLetterhead((prev) => ({ ...prev, country: e.target.value }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              USt-IdNr. / Steuernr.
              <input
                className="eh-input"
                value={letterhead.tax_id}
                onChange={(e) => setLetterhead((prev) => ({ ...prev, tax_id: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              E-Mail (für E-Rechnung)
              <input
                type="email"
                className="eh-input"
                value={letterhead.email}
                onChange={(e) => setLetterhead((prev) => ({ ...prev, email: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              IBAN
              <input
                className="eh-input"
                value={letterhead.iban}
                onChange={(e) => setLetterhead((prev) => ({ ...prev, iban: e.target.value }))}
                autoComplete="off"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              BIC
              <input
                className="eh-input"
                value={letterhead.bic}
                onChange={(e) => setLetterhead((prev) => ({ ...prev, bic: e.target.value }))}
                autoComplete="off"
              />
            </label>
            <p className="text-xs text-label-tertiary sm:col-span-2">
              IBAN/BIC und E-Mail werden in ZUGFeRD-PDFs und optionalen X-Rechnungen als
              Zahlungs- bzw. elektronische Adresse verwendet.
            </p>
            <BrandImageField
              label="Logo"
              hint="PNG, JPG, WebP, GIF, SVG oder ICO, höchstens 2 MB. Wird in Navigation, Login, Buchung, E-Mails und PDFs verwendet. Alternativ eine öffentlich erreichbare Bild-URL."
              value={letterhead.logo_url}
              canWrite={canWrite}
              uploading={brandImageUploading === "logo_url"}
              onFile={(file) => void onBrandImageSelected("logo_url", file)}
              onChange={(logo_url) => setLetterhead((prev) => ({ ...prev, logo_url }))}
              onClear={() => setLetterhead((prev) => ({ ...prev, logo_url: "" }))}
            />
            <BrandImageField
              label="Favicon"
              hint="Kleines quadratisches Bild für den Browser-Tab, z. B. 32×32 oder 180×180. Ohne eigenes Favicon wird das Logo verwendet."
              value={letterhead.favicon_url}
              canWrite={canWrite}
              uploading={brandImageUploading === "favicon_url"}
              onFile={(file) => void onBrandImageSelected("favicon_url", file)}
              onChange={(favicon_url) => setLetterhead((prev) => ({ ...prev, favicon_url }))}
              onClear={() => setLetterhead((prev) => ({ ...prev, favicon_url: "" }))}
            />
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              Fußzeile für Rechnungs-PDFs (optional)
              <textarea
                className="eh-input min-h-[4rem] resize-y"
                rows={3}
                value={letterhead.footer_text}
                onChange={(e) =>
                  setLetterhead((prev) => ({ ...prev, footer_text: e.target.value }))
                }
              />
            </label>
          </fieldset>
          <div className="rounded-lg border border-separator bg-grouped p-4 space-y-3">
            <div>
              <h3 className="text-sm font-medium">Briefpapier für Rechnungs-PDFs (optional)</h3>
              <p className="mt-1 text-xs text-label-tertiary">
                PDF mit Ihrem vorgedruckten Briefpapier (z. B. A4). Wird nur bei Rechnungen
                eingebunden, die per E-Mail verschickt werden. PDF-Vorschau, Download und Druck
                erfolgen ohne Briefpapier, damit Sie weiterhin auf Blankopapier drucken können.
              </p>
            </div>
            <input
              ref={letterheadPdfInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              disabled={!canWrite || letterheadPdfUploading}
              onChange={(e) => void onLetterheadPdfSelected(e)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={letterheadPreviewLoading}
                disabled={letterheadPreviewLoading}
                onClick={() => void openLetterheadPreview()}
              >
                Rechnungsvorschau mit Briefpapier
              </Button>
              {canWrite ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={letterheadPdfUploading}
                  disabled={letterheadPdfUploading}
                  onClick={() => letterheadPdfInputRef.current?.click()}
                >
                  {hasLetterheadPdf ? "Briefpapier ersetzen" : "Briefpapier hochladen"}
                </Button>
              ) : null}
              {hasLetterheadPdf && canWrite ? (
                <Button type="button" variant="ghost" size="sm" onClick={clearLetterheadPdf}>
                  Entfernen
                </Button>
              ) : null}
              {hasLetterheadPdf ? (
                <span className="text-sm text-status-green">Briefpapier hinterlegt</span>
              ) : (
                <span className="text-sm text-label-tertiary">Kein Briefpapier hinterlegt</span>
              )}
            </div>
            {hasLetterheadPdf ? (
              <object
                data={letterhead.letterhead_pdf_data}
                type="application/pdf"
                className="h-48 w-full rounded border border-separator bg-white"
                aria-label="Vorschau Briefpapier-PDF"
              >
                <p className="p-2 text-xs text-label-tertiary">
                  PDF-Vorschau wird in diesem Browser nicht unterstützt.
                </p>
              </object>
            ) : null}
          </div>
          {success ? (
            <p className="text-sm text-status-green">Grundeinstellungen gespeichert.</p>
          ) : null}
          {canWrite ? (
            <div className="flex justify-end">
              <Button type="submit" loading={saving} disabled={saving || brandImageUploading !== null}>
                Speichern
              </Button>
            </div>
          ) : null}
        </form>
      )}
    </Card>
  );
}
